import format from 'date-fns/format';
import { QuickButton } from '../conversation/types';
import { CaseSummary } from './caseSummary';
import { DEFAULT_PERSONALITY, PERSONALITIES, Personality, pickOne } from '../presets/personalities';

export const MAX_CASE_PICKS = 6;

/** Dates before this are placeholders (e.g. .NET's default 0001-01-01), not real values. */
const MIN_REAL_YEAR = 1900;

export function formatDate(value?: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime()) || date.getFullYear() < MIN_REAL_YEAR) return null;
  return format(date, 'MMM d, yyyy');
}

export function caseLink(c: Pick<CaseSummary, 'id'>): string | null {
  return c.id ? `/cases/${c.id}` : null;
}

/** The case's Messages tab, where the design team reads and replies. `focus=1` puts the cursor in the box. */
export function caseChatLink(c: Pick<CaseSummary, 'id'>): string | null {
  return c.id ? `/cases/${c.id}/case-chat?focus=1` : null;
}

/** "a", "a and b", "a, b and c". */
export function joinNatural(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** Status labels (from formatCaseStatus / the raw status) in the words a dentist would use. */
const STATUS_WORDS: Record<string, string> = {
  'Orders Received': 'received and waiting to start',
  'Case In Progress': 'in progress',
  'Finalizing Design': 'in final design',
  'Case For Dentist Approval': 'waiting for your approval',
  'Pending Clarification': 'waiting on a reply from you',
  Rescan: 'waiting on a rescan from you',
  Redesign: 'being redesigned',
  Shipped: 'shipped',
  Completed: 'completed',
  Cancelled: 'cancelled',
};

/** Provider steps (ServiceProviderType descriptions) in plain words. */
const PROVIDER_WORDS: Record<string, string> = {
  design: 'in design',
  'evident design center': 'in design',
  fabricate: 'in fabrication',
  'surgical kit': 'in surgical kit preparation',
};

/** Stages where the next move is the dentist's, so a missing due date is not worth mentioning. */
const WAITING_ON_DENTIST = new Set(['waiting for your approval', 'waiting on a reply from you', 'waiting on a rescan from you']);

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const daysBetween = (from: Date, to: Date): number => Math.max(0, Math.floor((to.getTime() - from.getTime()) / MS_PER_DAY));

const plural = (n: number, unit: string): string => `${n} ${unit}${n === 1 ? '' : 's'}`;

/** "Jane Doe · Crown and Night Guard", or "Case 1234" when there is no patient name. */
const subject = (c: CaseSummary): string => {
  const who = c.patientName || `Case ${c.caseRefNo}`;
  const what = joinNatural(c.treatments);
  return what ? `${who} · ${what}` : who;
};

/**
 * Where the case sits right now, as a predicate: "in design", "waiting for your approval". Anything the dentist
 * must act on comes first, then the provider step, then the status.
 */
export const stage = (c: CaseSummary): string | null => {
  if (c.flags?.rescan && !c.flags?.rescanUploaded) return STATUS_WORDS.Rescan;
  if (c.flags?.withApproval || c.statusLabel === 'Case For Dentist Approval') return STATUS_WORDS['Case For Dentist Approval'];
  if (c.statusLabel === 'Pending Clarification' || c.statusLabel === 'Rescan') return STATUS_WORDS[c.statusLabel];
  if (c.flags?.redesign || c.statusLabel === 'Redesign') return STATUS_WORDS.Redesign;
  if (c.latestProvider) return PROVIDER_WORDS[c.latestProvider.trim().toLowerCase()] || `in ${c.latestProvider.trim().toLowerCase()}`;
  if (c.statusLabel) return STATUS_WORDS[c.statusLabel] || `in ${c.statusLabel.toLowerCase()}`;
  return null;
};

/** The date to hold the lab to, and whether someone set it or the Hub estimated it. */
export const dueDate = (c: CaseSummary): { date: Date; estimated: boolean } | null => {
  const computed = parseRealDate(c.expectedReturnDate);
  if (computed) return { date: computed, estimated: c.expectedReturnSource !== undefined && c.expectedReturnSource !== 'scheduled' };
  const stored = parseRealDate(c.returnDate);
  return stored ? { date: stored, estimated: false } : null;
};

/**
 * Two or three short sentences, fact first: the stage in plain words, how long it has been there, and the due
 * date. A case past its date says so and ends with one call to action.
 *   "Jane Doe · Crown is in design. It's been there 3 days. Due Oct 12, 2026."
 *   "Jane Doe · Crown is in design. It's been there 9 days and is 4 days past its due date of Oct 12, 2026. Tap **Have support contact me** to flag it to the team."
 * Parts with missing data are left out.
 */
export function formatCaseDetail(c: CaseSummary, now: Date = new Date()): string {
  const completed = c.statusLabel === 'Completed' && !c.flags?.rescan;
  const where = completed ? STATUS_WORDS.Completed : stage(c);
  const updated = parseRealDate(c.lastUpdated);
  const due = completed || c.statusLabel === 'Cancelled' ? null : dueDate(c);
  const overdueDays = due && due.date.getTime() < now.getTime() ? daysBetween(due.date, now) : 0;
  const waitingOnDentist = where !== null && WAITING_ON_DENTIST.has(where);

  let text = where ? `${subject(c)} is ${where}` : subject(c);
  text += '.';

  if (updated) {
    const days = daysBetween(updated, now);
    if (days === 0) text += ' It moved today.';
    else text += waitingOnDentist ? ` It's been waiting ${plural(days, 'day')}.` : ` It's been there ${plural(days, 'day')}.`;
  }

  if (due && overdueDays > 0) {
    const label = due.estimated ? 'expected return' : 'due date';
    text += ` That is ${plural(overdueDays, 'day')} past its ${label} of ${format(due.date, 'MMM d, yyyy')}. Tap **Have support contact me** to flag it to the team.`;
  } else if (due) {
    text += due.estimated ? ` Expected back by ${format(due.date, 'MMM d, yyyy')}.` : ` Due ${format(due.date, 'MMM d, yyyy')}.`;
  } else if (where && !completed && !waitingOnDentist && c.statusLabel !== 'Cancelled') {
    text += ' No due date on file yet.';
  }

  const link = caseLink(c);
  return link ? `${text}\n[Open case](${link})` : text;
}

const parseRealDate = (value?: string): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) || date.getFullYear() < MIN_REAL_YEAR ? null : date;
};

/**
 * Why a case may feel slow, from what the Hub knows: who it is waiting on, whether it is inside its
 * expected window, and when it was last touched. Ends by pointing at the design team.
 */
export function formatDelayExplanation(c: CaseSummary, now: Date = new Date()): string {
  const lines: string[] = [];
  lines.push(`**Case ${c.caseRefNo}**${c.patientName ? ` · ${c.patientName}` : ''}`);

  const due = dueDate(c)?.date ?? null;
  const dueText = due ? format(due, 'MMM d, yyyy') : null;
  const updated = formatDate(c.lastUpdated);
  const updatedText = updated ? ` The last update was on ${updated}.` : '';
  const withProvider = c.latestProvider ? ` with ${c.latestProvider}` : '';

  if (c.flags?.rescan && !c.flags?.rescanUploaded) {
    lines.push(`This one is waiting on you: the lab asked for a rescan and can't carry on until a new scan is uploaded.${updatedText}`);
  } else if (c.flags?.withApproval || c.statusLabel === 'Case For Dentist Approval') {
    lines.push(`Good news, it isn't stuck. The design is done and waiting for your approval, so it moves on as soon as you approve it.${updatedText}`);
  } else if (c.statusLabel === 'Completed') {
    lines.push(`This one is actually marked as completed.${updatedText} If something still isn't right, the design team can sort it out.`);
  } else if (c.flags?.redesign) {
    lines.push(`It's being redesigned at the moment, which adds a bit of time, so the original dates may shift.${updatedText}`);
  } else if (due && due.getTime() >= now.getTime()) {
    lines.push(`It's still within its expected window: it should be back with you by ${dueText}.${updatedText}`);
  } else if (due) {
    lines.push(`You're right, it's past the expected return date of ${dueText}, sorry about that.${updatedText}`);
  } else {
    lines.push(`It's in progress${withProvider} and I can't see a specific hold-up from here.${updatedText}`);
  }

  lines.push("For a firmer date or to give the team a nudge, message them from the case page. They'll reply in the same place.");
  return lines.join('\n');
}

/** Every heading and hint across all voices; handy for tests. */
export const CASE_LIST_HEADINGS = PERSONALITIES.flatMap((p) => p.caseListHeadings);
export const CASE_LIST_HINTS = PERSONALITIES.flatMap((p) => p.caseListHints);

/** Numbered list of cases, one line each. The heading and hint are worded by the session's personality. */
export function formatCaseList(
  cases: CaseSummary[],
  firstName?: string,
  intro?: string,
  pick: () => number = Math.random,
  personality: Personality = DEFAULT_PERSONALITY,
): string {
  const heading = intro || pickOne(personality.caseListHeadings, pick)(firstName || '', cases.length);
  const rows = cases.map((c, i) => {
    const who = c.patientName ? ` · ${c.patientName}` : '';
    const status = c.statusLabel ? ` — ${c.statusLabel}` : '';
    return `${i + 1}. **${c.caseRefNo}**${who}${status}`;
  });
  return [heading, ...rows, '', pickOne(personality.caseListHints, pick)].join('\n');
}

export function caseQuickButtons(cases: CaseSummary[], max = MAX_CASE_PICKS): QuickButton[] {
  return cases.slice(0, max).map((c) => ({
    label: c.patientName ? `${c.caseRefNo} · ${c.patientName}` : c.caseRefNo,
    value: `case:${c.caseRefNo}`,
  }));
}
